import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'

import { env } from '../config/env'

export const apiClient = axios.create({
  baseURL: env.API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean }

const isAuthRequest = (url: string | undefined): boolean =>
  typeof url === 'string' && url.includes('/auth/')

const clearSessionAndRedirect = (): void => {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

const shouldEndSession = (refreshError: unknown): boolean => {
  if (!localStorage.getItem('refreshToken')) {
    return true
  }
  if (axios.isAxiosError(refreshError)) {
    const status = refreshError.response?.status
    return status === 401 || status === 403
  }
  return true
}

let refreshPromise: Promise<string> | null = null

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  const response = await axios.post(`${env.API_URL}/auth/refresh`, {
    refreshToken,
  })

  const { accessToken, refreshToken: rotatedRefreshToken } = response.data
  localStorage.setItem('accessToken', accessToken)
  if (rotatedRefreshToken) {
    localStorage.setItem('refreshToken', rotatedRefreshToken)
  }
  return accessToken
}

apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequest | undefined

    const canRetry =
      error.response?.status === 401 &&
      !!originalRequest &&
      !originalRequest._retry &&
      !isAuthRequest(originalRequest.url)

    if (!canRetry || !originalRequest) {
      return Promise.reject(error)
    }

    originalRequest._retry = true

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null
        })
      }
      const accessToken = await refreshPromise

      originalRequest.headers.Authorization = `Bearer ${accessToken}`
      return apiClient(originalRequest)
    } catch (refreshError) {
      if (shouldEndSession(refreshError)) {
        clearSessionAndRedirect()
      }
      return Promise.reject(refreshError)
    }
  }
)

export default apiClient
